import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let gisPromise = null;
const loadGis = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return gisPromise;
};

/**
 * Renders the official "Sign in with Google" button (with the Google icon),
 * exchanges the returned ID token with our backend, and logs the user in.
 */
const GoogleSignInButton = ({ onError, redirectTo = '/dashboard' }) => {
  const containerRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) {
      setUnavailable(true);
      return undefined;
    }
    let cancelled = false;

    loadGis()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;

        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: async (response) => {
            try {
              const { data } = await api.post('/auth/google', {
                credential: response.credential,
              });
              login(data.token, data.refresh_token, data.user);
              navigate(redirectTo);
            } catch (err) {
              onError?.(err.response?.data?.message || 'Google sign-in failed');
            }
          },
        });

        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'center',
          width: 320,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setUnavailable(true);
          onError?.('Could not load Google sign-in');
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (unavailable || !CLIENT_ID) return null;

  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}
      aria-label="Sign in with Google"
    />
  );
};

export default GoogleSignInButton;
